import { memo, useRef } from "react";
import { isValidQtyInput } from "./dailyGrid.utils";

export interface EditableCellProps {
  /** Grid coordinates — passed back to the (stable) grid handlers. */
  r: number;
  c: number;
  f: number;
  /** Committed (possibly edited) numeric value to display. */
  value: number;
  /** This cell is the one currently in edit mode. */
  isActive: boolean;
  /** Value differs from the original API value — render the edited highlight. */
  isEdited: boolean;
  /** Cell width in px (matches the scaled product sub-column width). */
  width: number;
  /** Font scale for the value/input (zoom of the product matrix). */
  fontScale: number;
  /** Text color for the value (per sub-field: sold/waste/leftover). */
  color?: string;
  /** Cell background (product-group tint), so the cell still tints. */
  background: string;
  borderRight?: boolean;
  /** Draw the 2px frozen-separator border on the left (first product sub-col). */
  borderLeft?: boolean;
  /** View-only: render a static cell with no edit affordances. */
  readOnly?: boolean;

  onActivate: (r: number, c: number, f: number) => void;
  /** Enter: commit and close. */
  onEnter: (r: number, c: number, f: number, raw: string) => void;
  /** Blur: commit and close (ignored by the grid during Tab/Escape). */
  onBlur: (r: number, c: number, f: number, raw: string) => void;
  /** Tab: commit and move to the next/prev editable cell. */
  onTab: (r: number, c: number, f: number, raw: string, dir: 1 | -1) => void;
  /** Escape: discard and close. */
  onEscape: () => void;
}

const EDITED_BG = "#FFF3CD";
const EDITED_BORDER = "#E0A800";

/**
 * One editable quantity cell. While active it renders an UNCONTROLLED <input>
 * (defaultValue + ref) so keystrokes never touch React state — only commit /
 * cancel / navigate do. Combined with stable grid handlers + React.memo, this
 * keeps editing one cell from re-rendering the rest of the grid.
 */
const EditableCell = memo(function EditableCell(props: EditableCellProps) {
  const {
    r,
    c,
    f,
    value,
    isActive,
    isEdited,
    width,
    fontScale,
    color,
    background,
    borderRight,
    borderLeft,
    readOnly,
    onActivate,
    onEnter,
    onBlur,
    onTab,
    onEscape,
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);
  const lastValidRef = useRef<string>("");

  const valueFont = `${(0.8 * fontScale).toFixed(3)}rem`;
  const tdBase: React.CSSProperties = {
    width,
    minWidth: width,
    maxWidth: width,
    height: 28,
    padding: 0,
    textAlign: "center",
    fontSize: valueFont,
    boxSizing: "border-box",
    borderBottom: "1px solid #ededf3",
    borderRight: borderRight ? "1px solid #e2e2ec" : "1px solid #ededf3",
    borderLeft: borderLeft ? "2px solid #c4c7d6" : undefined,
  };

  if (readOnly) {
    return (
      <td
        style={{
          ...tdBase,
          fontWeight: 400,
          color: value === 0 ? "#c4c4cf" : color,
          background,
        }}
      >
        {value}
      </td>
    );
  }

  if (isActive) {
    const read = () => inputRef.current?.value ?? "";
    return (
      <td style={{ ...tdBase, background: "#fff" }}>
        <input
          ref={inputRef}
          defaultValue={String(value)}
          autoFocus
          inputMode="decimal"
          aria-label="แก้ไขจำนวน"
          onFocus={(e) => {
            e.currentTarget.select();
            lastValidRef.current = e.currentTarget.value;
          }}
          onChange={(e) => {
            // Uncontrolled: enforce numeric / non-negative by reverting an
            // invalid keystroke to the last accepted value.
            if (isValidQtyInput(e.currentTarget.value)) {
              lastValidRef.current = e.currentTarget.value;
            } else {
              e.currentTarget.value = lastValidRef.current;
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onEnter(r, c, f, read());
            } else if (e.key === "Escape") {
              e.preventDefault();
              onEscape();
            } else if (e.key === "Tab") {
              e.preventDefault();
              onTab(r, c, f, read(), e.shiftKey ? -1 : 1);
            }
          }}
          onBlur={() => onBlur(r, c, f, read())}
          style={{
            width: "100%",
            height: "100%",
            border: `2px solid ${EDITED_BORDER}`,
            outline: "none",
            textAlign: "center",
            fontSize: valueFont,
            fontFamily: "inherit",
            boxSizing: "border-box",
            background: "#fff",
            padding: "0 2px",
          }}
        />
      </td>
    );
  }

  return (
    <td
      onClick={() => onActivate(r, c, f)}
      title="คลิกเพื่อแก้ไข"
      style={{
        ...tdBase,
        cursor: "pointer",
        fontWeight: isEdited ? 700 : 400,
        color: isEdited ? EDITED_BORDER : value === 0 ? "#c4c4cf" : color,
        background: isEdited ? EDITED_BG : background,
      }}
    >
      {value}
    </td>
  );
});

export default EditableCell;
